import { createFileRoute, useRouter } from "@tanstack/react-router";
import { getSiteOrigin } from "#/_packages/umbrella/config/utils.ts";
import { ContentRouteView } from "#/_packages/umbrella/features/routing/components/content-route-view";
import {
	getContentRouteHead,
	getContentRouteLoaderData,
} from "#/_packages/umbrella/features/routing/utils/content-route-utils";
import { validateSearch } from "#/_packages/umbrella/features/search/search-state";
import { discoverRendererTypes } from "#/_packages/umbrella/integrations/umbraco/utils/renderer-module";
import NotFoundComponent from "#/_packages/umbrella/lib/tanstack-router/components/NotFoundComponent";
import { rejectStandaloneFaqPage } from "#/app/features/faq/public-pages";
import {
	type SearchServerState,
	SearchServerStateContext,
} from "#/app/features/search/search-server-state";
import { getSearchServerState } from "#/app/features/search/search-ssr";

export const Route = createFileRoute("/_umbraco/_content/$")({
	validateSearch,
	loaderDeps: ({ search }) => ({ q: search.q }),
	staleTime: 5 * 60 * 1000,
	preloadStaleTime: 5 * 60 * 1000,
	component: Component,
	notFoundComponent: NotFoundComponent,
	loader: async ({ params, location, parentMatchPromise, deps }) => {
		const [data, parentMatch] = await Promise.all([
			getContentRouteLoaderData(params._splat, location),
			parentMatchPromise,
		]);
		await rejectStandaloneFaqPage(data.content?.contentType);
		const hasSearch =
			!data.isError &&
			[...discoverRendererTypes(data.content)].some(
				(type) => type === "searchUmbPage" || type === "searchResultsBlock",
			);
		const searchState: SearchServerState | undefined = hasSearch
			? JSON.parse(
					await getSearchServerState({
						data: {
							query: typeof deps.q === "string" ? deps.q : "",
						},
					}),
				)
			: undefined;
		return {
			...data,
			searchState,
			defaultCulture: parentMatch?.loaderData?.defaultCulture,
			globalSiteSettings: parentMatch?.loaderData?.globalSiteSettings,
		};
	},
	head: async ({ loaderData }) =>
		getContentRouteHead(
			loaderData?.content,
			loaderData?.globalSiteSettings,
			await getSiteOrigin(),
			loaderData?.defaultCulture,
		),
});

function Component() {
	const loaderData = Route.useLoaderData();
	const router = useRouter();

	return (
		<SearchServerStateContext value={loaderData.searchState}>
			<ContentRouteView
				loaderData={loaderData}
				onReset={() => router.invalidate()}
			/>
		</SearchServerStateContext>
	);
}
